import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../services/api";

// Async Thunks for Employee CRUD operations

// Get all employees
export const getAllEmps = createAsyncThunk(
  "emp/getAllEmps",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await api.get("/emps");
      return data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || error.message || "Failed to fetch employees"
      );
    }
  }
);

// Get my employees (created by logged-in user)
export const getMyEmps = createAsyncThunk(
  "emp/getMyEmps",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await api.get("/emps/my");
      return data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || error.message || "Failed to fetch your employees"
      );
    }
  }
);

// Get single employee by ID
export const getEmpById = createAsyncThunk(
  "emp/getEmpById",
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`/emp/${id}`);
      return data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || error.message || "Failed to fetch employee"
      );
    }
  }
);

// Create new employee
export const createEmp = createAsyncThunk(
  "emp/createEmp",
  async (empData, { rejectWithValue }) => {
    try {
      const { data } = await api.post("/emp", empData);
      return data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 
        error.response?.data?.errors?.join(", ") || 
        error.message || 
        "Failed to create employee"
      );
    }
  }
);

// Update employee
export const updateEmp = createAsyncThunk(
  "emp/updateEmp",
  async ({ id, empData }, { rejectWithValue }) => {
    try {
      const { data } = await api.put(`/emp/${id}`, empData);
      return data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 
        error.response?.data?.errors?.join(", ") || 
        error.message || 
        "Failed to update employee"
      );
    }
  }
);

// Delete employee
export const deleteEmp = createAsyncThunk(
  "emp/deleteEmp",
  async (id, { rejectWithValue }) => {
    try {
      await api.delete(`/emp/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || error.message || "Failed to delete employee"
      );
    }
  }
);

// Search employees
export const searchEmps = createAsyncThunk(
  "emp/searchEmps",
  async (searchQuery, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`/emps/search?search=${searchQuery}`);
      return data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || error.message || "Failed to search employees"
      );
    }
  }
);

// Get employees with pagination
export const getEmpsWithPagination = createAsyncThunk(
  "emp/getEmpsWithPagination",
  async ({ page = 1, limit = 10 }, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`/emps/paginated?page=${page}&limit=${limit}`);
      return data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || error.message || "Failed to fetch employees"
      );
    }
  }
);

// Employee Slice
const empSlice = createSlice({
  name: "emp",
  initialState: {
    emps: [],
    currentEmp: null,
    loading: false,
    error: null,
    success: false,
    message: null,
    pagination: {
      page: 1,
      limit: 10,
      total: 0,
      totalPages: 0,
    },
  },
  reducers: {
    clearErrors: (state) => {
      state.error = null;
    },
    clearMessage: (state) => {
      state.message = null;
    },
    resetSuccess: (state) => {
      state.success = false;
    },
    setCurrentEmp: (state, action) => {
      state.currentEmp = action.payload;
    },
    clearCurrentEmp: (state) => {
      state.currentEmp = null;
    },
  },
  extraReducers: (builder) => {
    // Get all employees
    builder.addCase(getAllEmps.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(getAllEmps.fulfilled, (state, action) => {
      state.loading = false;
      state.emps = action.payload;
      state.error = null;
    });
    builder.addCase(getAllEmps.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    // Get my employees
    builder.addCase(getMyEmps.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(getMyEmps.fulfilled, (state, action) => {
      state.loading = false;
      state.emps = action.payload;
      state.error = null;
    });
    builder.addCase(getMyEmps.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    // Get employee by ID
    builder.addCase(getEmpById.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(getEmpById.fulfilled, (state, action) => {
      state.loading = false;
      state.currentEmp = action.payload;
      state.error = null;
    });
    builder.addCase(getEmpById.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    // Create employee
    builder.addCase(createEmp.pending, (state) => {
      state.loading = true;
      state.error = null;
      state.success = false;
    });
    builder.addCase(createEmp.fulfilled, (state, action) => {
      state.loading = false;
      state.success = true;
      state.emps.unshift(action.payload);
      state.message = "Employee created successfully";
      state.error = null;
    });
    builder.addCase(createEmp.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
      state.success = false;
    });

    // Update employee
    builder.addCase(updateEmp.pending, (state) => {
      state.loading = true;
      state.error = null;
      state.success = false;
    });
    builder.addCase(updateEmp.fulfilled, (state, action) => {
      state.loading = false;
      state.success = true;
      state.message = "Employee updated successfully";
      state.emps = state.emps.map((emp) =>
        emp._id === action.payload._id ? action.payload : emp
      );
      if (state.currentEmp?._id === action.payload._id) {
        state.currentEmp = action.payload;
      }
      state.error = null;
    });
    builder.addCase(updateEmp.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
      state.success = false;
    });

    // Delete employee
    builder.addCase(deleteEmp.pending, (state) => {
      state.loading = true;
      state.error = null;
      state.success = false;
    });
    builder.addCase(deleteEmp.fulfilled, (state, action) => {
      state.loading = false;
      state.success = true;
      state.message = "Employee deleted successfully";
      state.emps = state.emps.filter(
        (emp) => emp._id !== action.payload
      );
      if (state.currentEmp?._id === action.payload) {
        state.currentEmp = null;
      }
      state.error = null;
    });
    builder.addCase(deleteEmp.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
      state.success = false;
    });

    // Search employees
    builder.addCase(searchEmps.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(searchEmps.fulfilled, (state, action) => {
      state.loading = false;
      state.emps = action.payload;
      state.error = null;
    });
    builder.addCase(searchEmps.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    // Get employees with pagination
    builder.addCase(getEmpsWithPagination.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(getEmpsWithPagination.fulfilled, (state, action) => {
      state.loading = false;
      state.emps = action.payload.data;
      state.pagination = {
        page: action.payload.page,
        limit: action.payload.limit || 10,
        total: action.payload.total,
        totalPages: action.payload.totalPages,
      };
      state.error = null;
    });
    builder.addCase(getEmpsWithPagination.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });
  },
});

export const {
  clearErrors,
  clearMessage,
  resetSuccess,
  setCurrentEmp,
  clearCurrentEmp,
} = empSlice.actions;

export default empSlice.reducer;

